import React, { useState, useEffect, useRef } from 'react';

import { Link, NavLink, useNavigate } from 'react-router-dom';
import styles from './Header.module.css';
import Modal from './Modal.jsx';
import { Menu, X } from 'lucide-react';

const Header = ({ announcementVisible }) => {
  const [navOpen, setNavOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [scrollingUp, setScrollingUp] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [forceMobile, setForceMobile] = useState(false);
  const currentHost = window.location.host.replace(/^www\./, '');
  const navigate = useNavigate();

  const headerContentRef = useRef(null);
  const logoLinkRef = useRef(null);
  const navListRef = useRef(null);
  const navRef = useRef(null);
  const rafIdRef = useRef(null);

  const toggleNav = () => setNavOpen(!navOpen);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY === 0);

      if (window.scrollY > lastScrollY) {
        setScrollingUp(false);
      } else {
        setScrollingUp(true);
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Detect when nav items exceed available width and force mobile menu
  useEffect(() => {
    const measureNavListWidth = () => {
      const nav = navRef.current;
      const navList = navListRef.current;
      if (!nav || !navList) return 0;

      // Temporarily reveal nav off-screen if it's hidden (e.g., in forceMobile)
      const prevNavDisplay = nav.style.display;
      const prevNavPosition = nav.style.position;
      const prevNavVisibility = nav.style.visibility;
      const prevNavLeft = nav.style.left;
      const prevNavTop = nav.style.top;

      const wasHiddenByCSS = getComputedStyle(nav).display === 'none';
      if (wasHiddenByCSS) {
        nav.style.display = 'flex';
        nav.style.position = 'absolute';
        nav.style.visibility = 'hidden';
        nav.style.left = '-99999px';
        nav.style.top = '-99999px';
      }

      const width = Math.ceil(navList.scrollWidth);

      // Revert inline styles
      if (wasHiddenByCSS) {
        nav.style.display = prevNavDisplay;
        nav.style.position = prevNavPosition;
        nav.style.visibility = prevNavVisibility;
        nav.style.left = prevNavLeft;
        nav.style.top = prevNavTop;
      }

      return width;
    };

    const computeShouldForceMobile = () => {
      const container = headerContentRef.current;
      const logo = logoLinkRef.current;
      if (!container || !logo) return;

      const containerWidth = container.clientWidth;
      const logoWidth = logo.offsetWidth;
      const safetyGap = 40; // px
      const navWidth = measureNavListWidth();
      const neededWidth = logoWidth + safetyGap + navWidth;

      // Hysteresis margins to prevent flapping around the threshold
      const enterMargin = 20; // require 20px overflow to enter mobile
      const exitMargin = 20;  // require 20px spare to exit mobile

      // Hard breakpoint to bias tablets into mobile nav regardless of overflow math
      const hardBreakpoint = 900; // px of container width
      if (containerWidth < hardBreakpoint) {
        if (!forceMobile) setForceMobile(true);
        return;
      }

      if (!forceMobile) {
        const shouldEnter = neededWidth > (containerWidth + enterMargin);
        if (shouldEnter) setForceMobile(true);
      } else {
        const shouldExit = neededWidth < (containerWidth - exitMargin);
        if (shouldExit) {
          setForceMobile(false);
          setNavOpen(false); // ensure overlay closes when returning to desktop
        }
      }
    };

    computeShouldForceMobile();

    const resizeObserver = new ResizeObserver(() => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        computeShouldForceMobile();
      });
    });

    if (headerContentRef.current) {
      resizeObserver.observe(headerContentRef.current);
    }
    if (navListRef.current) {
      resizeObserver.observe(navListRef.current);
    }

    const onResize = () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        computeShouldForceMobile();
      });
    };

    window.addEventListener('resize', onResize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', onResize);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (navOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [navOpen]);

  const handleLinkClick = (e) => {
    setNavOpen(false);
  };

  const handleCatalogClick = (e) => {
    e.preventDefault();
    setCatalogModalOpen(true);
  };

  const headerClassName = `
    ${styles.header}
    ${(scrollingUp || isAtTop) ? styles.visible : styles.hidden}
    ${isAtTop ? styles.atTop : ''}
    ${navOpen ? styles.navOpen : ''}
    ${forceMobile ? styles.forceMobile : ''}
    ${announcementVisible ? styles.announcementSpacing : ''}
  `.trim();

  return (
    <>
      <header className={headerClassName}>
        <div className={styles.headerContent} ref={headerContentRef}>
          <Link to="/" className={styles.logoLink} ref={logoLinkRef}>
            <img src="/logo.svg" alt="Logo" className={styles.logo} />
          </Link>

          <button
            className={styles.hamburger}
            onClick={toggleNav}
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
          >
            {navOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <nav className={`${styles.nav} ${navOpen ? styles.navOpen : ''}`} ref={navRef}>
            <ul className={styles.navList} ref={navListRef}>
              <li>
                <NavLink
                  end
                  to="/"
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                  onClick={handleLinkClick}
                >
                  Home
                </NavLink>
              </li>
              <li className={styles.dropdown}>
                <NavLink
                  to="/products"
                  className={styles.navLink}
                  onClick={handleLinkClick}
                >
                  Products & Services
                </NavLink>
                <ul className={`${styles.dropdownContent} ${styles.megaMenu} ${styles.megaGrid}`}>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/aluminum-railings"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Aluminum Railings
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/iron-railings"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Iron Railings
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/glass-railings"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Glass Railings
                    </NavLink>
                  </li>                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/cable-railings"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Cable Railings
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/staircases"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Staircases
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/spiral-staircases"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Spiral Staircases
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/metal-fencing"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Metal Fencing
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/custom-fabrications"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Custom Fabrications
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/products/vinyl-decking"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Vinyl Decking
                    </NavLink>
                    <NavLink
                      to="/products/powder-coating"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Powder Coating
                    </NavLink>
                  </li>
                </ul>
              </li>
              <li className={styles.dropdown}>
                <NavLink
                  to="/portfolios"
                  className={styles.navLink}
                  onClick={handleLinkClick}
                >
                  Portfolios
                </NavLink>
                <ul className={`${styles.dropdownContent} ${styles.megaMenu} ${styles.megaMenuNarrow} ${styles.megaGrid}`}>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/architectural"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Architectural Series
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to={`https://bgrs.${currentHost}`}
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleLinkClick}
                    >
                      Builder Series <span className={styles.externalIcon}>↗</span>
                    </NavLink>
                  </li>
                  <li className={styles.megaItem}>
                    <NavLink
                      to="/commercial"
                      className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                      onClick={handleLinkClick}
                    >
                      Commercial Series
                    </NavLink>
                  </li>
                </ul>
              </li>
              <li>
                <NavLink
                  to="/resources"
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                  onClick={handleLinkClick}
                >
                  Resources
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/about"
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                  onClick={handleLinkClick}
                >
                  About
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contact"
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.activeLink : ''}`}
                  onClick={handleLinkClick}
                >
                  Contact
                </NavLink>
              </li>
              <li>
                <button
                  className={`${styles.navLink} ${styles.catalogButton}`}
                  onClick={handleCatalogClick}
                >
                  Catalogs
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {catalogModalOpen && (
        <Modal onClose={() => setCatalogModalOpen(false)}>
          <div className={styles.catalogOptions}>
            <h2>Select a Catalog</h2>
            <div className={styles.catalogGrid}>
              {[
                { title: 'Complete Product Line Catalog', file: 'ABC Railing - Product Catalog.pdf' },
                { title: 'Architectural Series', file: 'ABC Railing - Architectural Catalog.pdf' },
                { title: 'Builder Series', file: 'ABC Railing - Builder Catalog.pdf' },
                { title: 'Commercial Series', file: 'ABC Railing - Commercial Catalog.pdf' },
              ].map((catalog) => (
                <a
                  key={catalog.title}
                  href={`/${catalog.file}`}
                  className={styles.catalogCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{catalog.title}</span>
                </a>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Header;